import React from 'react';
import Image from 'next/image';

export interface ProductDetail {
  id: string; // e.g., 'dj-sales', 'dj-influence'
  modalTitle: string;
  modalContent: React.ReactNode;
}

export const productDetailsData: ProductDetail[] = [
  {
    id: 'dj-sales',
    modalTitle: 'DJ-SALES',
    modalContent: (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-2 prose prose-invert max-w-none text-gray-300 prose-a:text-[#59b9c1]">
          <h2 className="text-2xl font-bold text-[#59b9c1] mt-0 mb-4">
            Advanced Sales Segmentation
          </h2>
          <ul className="list-disc list-inside space-y-1 my-2 ml-4">
            <li>
              <strong>Digital footprint:</strong> DJ-SALES uses digital footprints to prioritize
              high-potential HCPs and employs AI to segment and target with greater precision,
              accelerating new market launches.
            </li>
            <li>
              <strong>Individual sales potential:</strong> It assesses the individual sales
              potential of each doctor, enabling more accurate and efficient market segmentation.
            </li>
            <li>
              <strong>Optimize resources:</strong> DJ-SALES optimizes sales resources and
              strategies, allowing for better allocation of sales teams, channels, and communication
              frequency.
            </li>
            <li>
              <strong>Stronger relationships:</strong> This leads to stronger HCP relationships,
              improved satisfaction, loyalty, and adaptation of sales strategies to changing market
              conditions.
            </li>
          </ul>
        </div>
        <div className="md:col-span-1 flex flex-col items-center justify-start text-center">
          <Image
            src="/images/DJ-SALES.jpg"
            alt="DJ Sales illustration"
            width={300}
            height={200}
            className="rounded-lg shadow-md max-w-full h-auto object-contain max-h-60 mb-2"
          />
          <p className="text-xs italic text-gray-400">
            Advanced sales segmentation is the key to selling your products, treatments, and
            technologies to the doctors who are most likely to prescribe them.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'dj-influence',
    modalTitle: 'DJ-INFLUENCE',
    modalContent: (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-2 prose prose-invert max-w-none text-gray-300 prose-a:text-[#59b9c1]">
          <h2 className="text-2xl font-bold text-[#59b9c1] mt-0 mb-4">Identify your Influencers</h2>
          <li>
            <strong>Digital footprint:</strong> DJ-INFLUENCE identifies key influencers and digital
            opinion leaders (DOLs) within specific healthcare markets, helping to understand and
            leverage their impact.
          </li>
          <li>
            <strong>Influencers:</strong> Influencers in the pharma industry can significantly sway
            the views and actions of others through their authority, reputation, or charisma.
          </li>
          <li>
            <strong>Advocacy:</strong> This tool assists in understanding how new products and
            treatments are perceived and adopted providing insights to enhance marketing and sales
            strategies and improve the perception, adoption, and advocacy of their brand and
            product.
          </li>
        </div>
        <div className="md:col-span-1 flex flex-col items-center justify-start text-center">
          <Image
            src="/images/DJ-INFLUENCE.jpg"
            alt="DJ Influence illustration"
            width={300}
            height={200}
            className="rounded-lg shadow-md max-w-full h-auto object-contain max-h-60 mb-2"
          />
          <p className="text-xs italic text-gray-400">
            By identifying and engaging with both KOLs and DOLs, you can create value for both your
            company and your customers.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'dj-channel',
    modalTitle: 'DJ-CHANNEL',
    modalContent: (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-2 prose prose-invert max-w-none text-gray-300 prose-a:text-[#59b9c1]">
          <h2 className="text-2xl font-bold text-[#59b9c1] mt-0 mb-4">
            Use the Preferred Channel for Each HCP
          </h2>
          <li>
            <strong>Communication channels:</strong> DJ-CHANNEL identifies each HCP&apos;s preferred
            communication channels, enabling consistent experiences, relevant content delivery,
            optimized marketing resources, and personalized interactions.
          </li>
          <li>
            <strong>Individual preferences:</strong> It acknowledges that HCPs have varying
            preferences (face-to-face, video, phone, email) and respects these preferences to
            improve satisfaction and loyalty.
          </li>
          <li>
            <strong>Align with HCP needs:</strong> This tool allows pharmaceutical companies to
            provide targeted content that aligns with HCP needs, increasing engagement and
            conversion rates.
          </li>
          <li>
            <strong>Omnichannel strategy:</strong> DJ-CHANNEL facilitates omnichannel marketing
            strategies, ensuring the right message is delivered through the right channel at the
            right time, fostering stronger relationships.
          </li>
        </div>
        <div className="md:col-span-1 flex flex-col items-center justify-start text-center">
          <Image
            src="/images/DJ-CHANNEL.jpg"
            alt="DJ Channel illustration"
            width={300}
            height={200}
            className="rounded-lg shadow-md max-w-full h-auto object-contain max-h-60 mb-2"
          />
          <p className="text-xs italic text-gray-400">
            Using DJ-CHANNEL can help you to improve customer satisfaction, loyalty, and advocacy.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'dj-mind',
    modalTitle: 'DJ-MIND',
    modalContent: (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-2 prose prose-invert max-w-none text-gray-300 prose-a:text-[#59b9c1]">
          <h2 className="text-2xl font-bold text-[#59b9c1] mt-0 mb-4">
            Personalisation at the Next Level
          </h2>
          <li>
            <strong>Personality type:</strong> DJ-MIND identifies the dominant personality type
            (Manager, Inspiring, Nourish, Detail) and secondary preferences of HCPs for deeper
            personalization, enhancing marketing effectiveness.
          </li>
          <li>
            <strong>Effective communication:</strong> This approach acknowledges that understanding
            an HCP&apos;s personality is crucial for effective communication and engagement.
          </li>
          <li>
            <strong>Improve customer interactions:</strong> By recognizing personality traits,
            pharmaceutical companies can tailor their interactions to individual needs. resonate
            with each individual, improving customer satisfaction, loyalty, and advocacy.
          </li>
        </div>
        <div className="md:col-span-1 flex flex-col items-center justify-start text-center">
          <Image
            src="/images/DJ-MIND.jpg"
            alt="DJ Mind illustration"
            width={300}
            height={200}
            className="rounded-lg shadow-md max-w-full h-auto object-contain max-h-60 mb-2"
          />
          <p className="text-xs italic text-gray-400">
            Understanding customer personality improves satisfaction, loyalty, and advocacy.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'dj-persona',
    modalTitle: 'DJ-PERSONA',
    modalContent: (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-2 prose prose-invert max-w-none text-gray-300 prose-a:text-[#59b9c1]">
          <h2 className="text-2xl font-bold text-[#59b9c1] mt-0 mb-4">
            Identify the Persona for Each of Your HCPs
          </h2>
          <li>
            <strong>Match to persona:</strong> DJ-PERSONA provides a multi-faceted view of each HCP
            by matching them to one of 4 personas based on their digital footprint, aligning with
            and enriching client&apos;s market research segments.
          </li>
          <li>
            <strong>Understand your customer:</strong> This tool helps pharmaceutical companies
            understand the needs, expectations, and motivations of their customers, enabling them to
            tailor their products, services, and messages more effectively.
          </li>
          <li>
            <strong>Targeted engagement:</strong> DJ-PERSONA offers valuable insights for targeted
            engagement strategies, creating a deeper understanding of individual preferences and
            behaviors to improve customer satisfaction and loyalty.
          </li>
        </div>
        <div className="md:col-span-1 flex flex-col items-center justify-start text-center">
          <Image
            src="/images/DJ-PERSONA.jpg"
            alt="DJ Persona illustration"
            width={300}
            height={200}
            className="rounded-lg shadow-md max-w-full h-auto object-contain max-h-60 mb-2"
          />
          <p className="text-xs italic text-gray-400">
            Understanding customer personas helps create more effective engagement strategies.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'dj-adopter',
    modalTitle: 'DJ-ADOPTER',
    modalContent: (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-2 prose prose-invert max-w-none text-gray-300 prose-a:text-[#59b9c1]">
          <h2 className="text-2xl font-bold text-[#59b9c1] mt-0 mb-4">
            Who Are Your Early Adopters?
          </h2>
          <li>
            <strong>Early adopters:</strong> DJ-ADOPTER identifies HCPs who are early adopters,
            enabling companies to test innovations, gain feedback, leverage influence, and grow
            markets.
          </li>
          <li>
            <strong>Crucial for product launch:</strong> These early adopters are crucial in the
            pharmaceutical industry as they are often willing to try new products and provide
            valuable feedback.
          </li>
          <li>
            <strong>Engage early:</strong> Understanding these individuals allows companies to
            effectively engage with them, test new products, and gather insights, accelerating the
            adoption of new therapies and improving patient outcomes.
          </li>
        </div>
        <div className="md:col-span-1 flex flex-col items-center justify-start text-center">
          <Image
            src="/images/DJ-ADOPTER.jpg"
            alt="DJ Adopter illustration"
            width={300}
            height={200}
            className="rounded-lg shadow-md max-w-full h-auto object-contain max-h-60 mb-2"
          />
          <p className="text-xs italic text-gray-400">
            With DJ-ADOPTER you can create value for both you and your customers.
          </p>
        </div>
      </div>
    ),
  },
  // Add other product details here if they have specific modal content
  // Example placeholder for DJ-FOCUS
  {
    id: 'dj-focus',
    modalTitle: 'DJ-FOCUS',
    modalContent: (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-2 prose prose-invert max-w-none text-gray-300 prose-a:text-[#59b9c1]">
          <h2 className="text-2xl font-bold text-[#59b9c1] mt-0 mb-4">
            Understand HCP Focus Areas
          </h2>
          <li>What is the highest focus for each HCP? (Business | Academic | Education)</li>
          <li>Know their focus and start building programmes to support them.</li>
          <li>(Placeholder - add more detailed content)</li>
        </div>
        <div className="md:col-span-1 flex flex-col items-center justify-start text-center">
          <Image
            src="/images/placeholder.jpg"
            alt="DJ Focus illustration"
            width={300}
            height={200}
            className="rounded-lg shadow-md max-w-full h-auto object-contain max-h-60 mb-2"
          />
        </div>
      </div>
    ),
  },
  // Add placeholders for other products (DJ-AFFINITY, DJ-NETWORK, etc.) similarly
  // ...
];
